import api from './api';

// Types for authentication
export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  grade: number;
  school: string;
  schoolRegistrationNumber: string;
  phone: string;
  parentName: string;
  parentPhone: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    student: any;
    token: string;
    expiresIn: string;
  };
  error?: string;
}

export interface Student {
  _id: string;
  name: string;
  email: string;
  grade: number;
  school: string;
  schoolRegistrationNumber: string;
  phone: string;
  parentName: string;
  parentPhone: string;
  role: 'student' | 'admin';
  preferences: {
    subjects: string[];
    difficultyPreference: string;
    notifications: {
      email: boolean;
      sms: boolean;
      testReminders: boolean;
      resultUpdates: boolean;
    };
  };
  stats: {
    totalTests: number;
    averageScore: number;
    totalTimeSpent: number;
    strongSubjects: string[];
    weakSubjects: string[];
    streak: number;
    lastTestDate: string | null;
    bestScore: number;
    improvementRate: number;
  };
  isActive: boolean;
  emailVerified: boolean;
  registrationDate: string;
  lastLoginDate: string | null;
}

class AuthService {
  private readonly baseUrl = '/auth';
  private readonly TOKEN_KEY = 'apex_auth_token';
  private readonly STUDENT_KEY = 'apex_student_data';

  /**
   * Register a new student
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>(`${this.baseUrl}/register`, data);
      
      if (response.data.success && response.data.data) {
        // Store token and student data
        this.setToken(response.data.data.token);
        this.setStudent(response.data.data.student);
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  }

  /**
   * Login student
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>(`${this.baseUrl}/login`, data);
      
      if (response.data.success && response.data.data) {
        // Store token and student data
        this.setToken(response.data.data.token);
        this.setStudent(response.data.data.student);
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  }

  /**
   * Logout student
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint if token exists
      if (this.getToken()) {
        await api.post(`${this.baseUrl}/logout`);
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local storage
      this.clearAuth();
    }
  }

  /**
   * Get current student profile
   */
  async getProfile(): Promise<Student> {
    try {
      const response = await api.get<{ success: boolean; data: { student: Student } }>(
        `${this.baseUrl}/profile`
      );
      
      if (response.data.success && response.data.data) {
        // Update stored student data
        this.setStudent(response.data.data.student);
        return response.data.data.student;
      }
      
      throw new Error('Failed to fetch profile');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch profile');
    }
  }

  /**
   * Update student profile
   */
  async updateProfile(updates: Partial<Student>): Promise<Student> {
    try {
      const response = await api.put<{ success: boolean; data: { student: Student } }>(
        `${this.baseUrl}/profile`,
        updates
      );
      
      if (response.data.success && response.data.data) {
        // Update stored student data
        this.setStudent(response.data.data.student);
        return response.data.data.student;
      }
      
      throw new Error('Failed to update profile');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update profile');
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const response = await api.put(`${this.baseUrl}/change-password`, {
        currentPassword,
        newPassword
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to change password');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to change password');
    }
  }

  /**
   * Verify if current token is valid
   */
  async verifyToken(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) return false;

      const response = await api.get(`${this.baseUrl}/verify-token`);
      
      if (response.data.success && response.data.data) {
        // Update stored student data
        this.setStudent(response.data.data.student);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      this.clearAuth();
      return false;
    }
  }

  /**
   * Get dashboard stats
   */
  async getDashboardStats(): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}/dashboard-stats`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to fetch dashboard stats');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch dashboard stats');
    }
  }

  // Token management
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  // Student data management
  setStudent(student: Student): void {
    localStorage.setItem(this.STUDENT_KEY, JSON.stringify(student));
  }

  getStudent(): Student | null {
    const studentData = localStorage.getItem(this.STUDENT_KEY);
    return studentData ? JSON.parse(studentData) : null;
  }

  removeStudent(): void {
    localStorage.removeItem(this.STUDENT_KEY);
  }

  // Clear all auth data
  clearAuth(): void {
    this.removeToken();
    this.removeStudent();
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Get current student from storage
  getCurrentStudent(): Student | null {
    return this.getStudent();
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService; 